import isString from 'lodash/isString';
// @ts-expect-error - This file must be remove after migration and not be used in production S2CX-13007
import * as ReactRouterDom from 'react-router-real';
// @ts-expect-error - This file must be remove after migration and not be used in production S2CX-13007
import type { To } from 'react-router-real';

const extractRawBase = (currentPath: string): string =>
  currentPath.split(/[?#]/)[0];

const getOrdIdPrefix = (rawBase: string): string => {
  const segments = rawBase.split('/').filter(Boolean);
  return segments.length ? `/${segments[0]}` : '';
};

const normalizeCurrentBase = (rawBase: string, orgIdPrefix: string): string =>
  rawBase === orgIdPrefix + '/' ? orgIdPrefix : rawBase;

const parsePathParts = (
  path: string,
): {
  pathPart: string;
  queryPart: string;
  hashPart: string;
} => {
  const queryIndex = path.indexOf('?');
  const hashIndex = path.indexOf('#');
  if (queryIndex !== -1 && (hashIndex === -1 || queryIndex < hashIndex))
    return {
      pathPart: path.substring(0, queryIndex),
      queryPart: path.substring(
        queryIndex,
        hashIndex === -1 ? undefined : hashIndex,
      ),
      hashPart: hashIndex !== -1 ? path.substring(hashIndex) : '',
    };
  if (hashIndex !== -1)
    return {
      pathPart: path.substring(0, hashIndex),
      queryPart: '',
      hashPart: path.substring(hashIndex),
    };
  return { pathPart: path, queryPart: '', hashPart: '' };
};

const removeBaseUrl = (pathPart: string, base: string): string =>
  pathPart.startsWith(base) ? pathPart.slice(base.length) : pathPart;

const hasUuidPrefix = (pathPart: string): boolean =>
  /^\/[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}/.test(
    pathPart,
  );

const prependOrgId = (orgId: string, pathPart: string): string =>
  pathPart.startsWith('/') ? orgId + pathPart : orgId + '/' + pathPart;

export const rewritePath = (
  path: string,
  currentPath = window.location.pathname,
  base = window.location.origin,
): string => {
  const rawCurrentBase = extractRawBase(currentPath);
  const orgIdPrefix = getOrdIdPrefix(rawCurrentBase);
  const currentBase = normalizeCurrentBase(rawCurrentBase, orgIdPrefix);
  const { pathPart: rawPathPart, queryPart, hashPart } = parsePathParts(path);
  const pathPartAfterBaseRemoval = removeBaseUrl(rawPathPart, base);
  return rawPathPart === ''
    ? currentBase + queryPart + hashPart
    : hasUuidPrefix(pathPartAfterBaseRemoval)
      ? pathPartAfterBaseRemoval + queryPart + hashPart
      : orgIdPrefix && pathPartAfterBaseRemoval.startsWith(orgIdPrefix)
        ? pathPartAfterBaseRemoval + queryPart + hashPart
        : prependOrgId(orgIdPrefix, pathPartAfterBaseRemoval) +
          queryPart +
          hashPart;
};

export const useNavigate: typeof ReactRouterDom.useNavigate = () => {
  const originalNavigate = ReactRouterDom.useNavigate();

  const navigateFn: ReactRouterDom.NavigateFunction = (
    to: To | number,
    options?: ReactRouterDom.NavigateOptions,
  ) => {
    if (Number.isInteger(to)) {
      return originalNavigate(to, options);
    }

    if (isString(to) && !to.startsWith('/')) {
      return originalNavigate(to, options);
    }

    if (isString(to)) {
      return originalNavigate(rewritePath(to), options);
    }

    return originalNavigate(
      {
        pathname: rewritePath(to.pathname),
        search: to.search,
        hash: to.hash,
      },
      options,
    );
  };

  return navigateFn;
};

export const generatePath: typeof ReactRouterDom.generatePath = (
  path: string,
  params: any,
) => {
  const originalPath = ReactRouterDom.generatePath(path, params);
  const rewrittenPath = rewritePath(originalPath);

  return rewrittenPath;
};

export const useMatch: typeof ReactRouterDom.useMatch = (
  pattern:
    | ReactRouterDom.PathPattern<ReactRouterDom.Path>
    | ReactRouterDom.Path,
) => {
  const modifiedPattern =
    typeof pattern === 'string' ? `/:organizationId${pattern}` : pattern;

  return ReactRouterDom.useMatch(modifiedPattern);
};

// @ts-expect-error - This file must be remove after migration and not be used in production S2CX-13007
export * from 'react-router-real';
